import React from "react"
import FieldErrors from "../shared/field_errors"
import type { IdentifierType } from "./models"
const LABELS: Record<IdentifierType, string> = {
  bundleId: "Bundle ID",
  cdhash: "Code Directory Hash",
  teamId: "Team ID",
  signingId: "Signing ID",
  pathPrefix: "Path Prefix",
}

const PLACEHOLDERS: Record<IdentifierType, string> = {
  bundleId: "e.g. com.apple.Safari",
  cdhash: "40-character hexadecimal digest",
  teamId: '10-character Team ID, or "*APPLE*"',
  signingId: "e.g. com.apple.Safari",
  pathPrefix: "e.g. /Applications/Slack.app/",
}

interface IdentifierRowProps<Type extends IdentifierType> {
  prefix: React.ReactNode
  rowId: string
  name?: string
  typeName?: string
  type: Type | ""
  value: string
  options: Type[]
  blankOption?: string
  removeIcon: string
  removeLabel?: string
  errors?: string[]
  describedBy?: string
  disabled: boolean
  onChangeType: (type: Type | "") => void
  onChangeValue: (value: string) => void
  onRemove: () => void
}

export default function IdentifierRow<Type extends IdentifierType>({
  prefix,
  rowId,
  name,
  typeName,
  type,
  value,
  options,
  blankOption,
  removeIcon,
  removeLabel,
  errors,
  describedBy,
  disabled,
  onChangeType,
  onChangeValue,
  onRemove,
}: IdentifierRowProps<Type>) {
  const removeName = removeLabel || (type ? `Remove ${LABELS[type]}` : "Remove")
  const errorId = errors?.length ? `${rowId}-error` : undefined
  const valueDescribedBy =
    [describedBy, errorId].filter(Boolean).join(" ") || undefined

  const row = (
    <div className="binary-rule-identifier">
      {prefix}

      <select
        id={`${rowId}-type`}
        aria-label="Identifier type"
        className="form-control binary-rule-type-select"
        name={typeName}
        value={type}
        disabled={disabled}
        onChange={event => onChangeType(event.target.value as Type | "")}
      >
        {blankOption && <option value="">{blankOption}</option>}
        {options.map(option => (
          <option key={option} value={option}>
            {LABELS[option]}
          </option>
        ))}
      </select>

      <input
        type="text"
        id={rowId}
        aria-label={type ? LABELS[type] : "Identifier value"}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={valueDescribedBy}
        className="form-control binary-rule-value"
        name={name}
        placeholder={type ? PLACEHOLDERS[type] : ""}
        value={value}
        disabled={disabled || !type}
        onChange={event => onChangeValue(event.target.value)}
      />

      {!disabled && (
        <button
          type="button"
          className="btn btn-link binary-rule-remove"
          aria-label={removeName}
          onClick={onRemove}
        >
          <span className={`glyphicon ${removeIcon}`} />
          {removeLabel ? ` ${removeLabel}` : null}
        </button>
      )}
    </div>
  )

  if (!errors?.length) {
    return row
  }

  return (
    <div className="has-error">
      {row}
      <FieldErrors errors={errors} id={errorId} />
    </div>
  )
}
